import React, { useContext } from 'react';

import Button from '~/components/ui/Button';
import { ContainerGrid } from '~/components/layout/PageStructure';
import PropTypes from 'prop-types';
import UIContext from '~/context/UIContext';
import cn from 'classnames';
import { createMarkup } from '~/utils/helpers';
import { placeholderImage } from '~/utils/placeholders';
import { ResponsiveImage } from '~/components/ui/molecules/ResponsiveImage';

const ImageWithText = ({ data = {} }) => {
  const { settings = {} } = data;
  const {
    layoutRatio = '1:1',
    imagePlacement = 'left',
    image = placeholderImage('portraitMedium'),
    title = 'Your image with text block',
    description = 'Write some engaging content!',
    uri = '#',
    cta = 'Call to action',
  } = settings;

  const { breakpoint } = useContext(UIContext);

  const TextBlock = ({ className }) => (
    <div
      className={cn(
        'col-span-12 self-center flex flex-col items-start justify-center mt-3 md:mt-0',
        className,
      )}
    >
      {title && <h3 className="mb-5 text-h2 lg:text-h2-lg">{title}</h3>}
      {description && (
        <p
          className="mb-7 text-body lg:text-subtitle-lg"
          dangerouslySetInnerHTML={createMarkup(description)}
        />
      )}
      {uri && cta && <Button attrs={{ to: uri }} button={{ text: cta }} />}
    </div>
  );

  const ImageBlock = ({ className }) =>
    image && (
      <div className={cn('col-span-12 self-center', className)}>
        <ResponsiveImage
          url={image.url}
          altText={title}
          aspectRatio="natural"
          className="w-full overflow-hidden rounded"
          width={1200}
          media={[
            {
              width: 700,
              media: '(min-width: 1100px)',
              useAsFallback: true,
            },
            {
              width: 500,
              media: '(min-width: 900px)',
            },
            {
              width: 350,
              media: '(min-width: 768px)',
            },
            {
              width: 720,
              media: '(min-width: 501px)',
            },
            {
              width: 500,
              media: '(max-width:500px)',
            },
          ]}
        />
      </div>
    );

  return (
    <div className="py-7">
      <ContainerGrid>
        {(imagePlacement === 'left' || breakpoint.isTabletOrSmaller) && (
          <>
            <ImageBlock
              className={cn({
                'md:col-span-6': layoutRatio === '1:1',
                'md:col-span-7': layoutRatio === '2:1',
                'md:col-span-4 lg:col-start-2': layoutRatio === '2:3',
              })}
            />
            <TextBlock
              className={cn({
                'md:col-span-6 md:col-start-7 lg:col-span-5 lg:col-start-8':
                  layoutRatio === '1:1',
                'md:col-span-4 md:col-start-9': layoutRatio === '2:1',
                'md:col-start-6 md:col-span-7 lg:col-span-5 lg:col-start-7':
                  layoutRatio === '2:3',
              })}
            />
          </>
        )}
        {imagePlacement === 'right' && !breakpoint.isTabletOrSmaller && (
          <>
            <TextBlock
              className={cn({
                'md:col-span-6 lg:col-span-5': layoutRatio === '1:1',
                'md:col-span-4': layoutRatio === '2:1',
                'md:col-span-7 lg:col-start-2 lg:col-span-5':
                  layoutRatio === '2:3',
              })}
            />
            <ImageBlock
              className={cn({
                'md:col-span-6 md:col-start-7': layoutRatio === '1:1',
                'md:col-span-7 md:col-start-6': layoutRatio === '2:1',
                'md:col-span-4 md:col-start-9 lg:col-start-8':
                  layoutRatio === '2:3',
              })}
            />
          </>
        )}
      </ContainerGrid>
    </div>
  );
};

ImageWithText.propTypes = {
  data: PropTypes.shape({
    layoutRatio: PropTypes.string,
    imagePlacement: PropTypes.string,
    image: PropTypes.shape({
      imageUrl: PropTypes.string.isRequired,
    }),
    title: PropTypes.string,
    description: PropTypes.string,
    cta: PropTypes.string,
    uri: PropTypes.string,
  }),
};

export default ImageWithText;
